import { Chevron } from "@/components/ui/Chevron";
import type { EventEnvelope } from "@/lib/api/types";

/**
 * Chat-style event log. One row per SSE event. The chevron motif marks
 * each row; the row colour shifts subtly for error / terminal events.
 */
export function Timeline({ events }: { events: EventEnvelope[] }) {
  if (events.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
        <Chevron pulse className="mr-2" />
        Waiting for the agent to start…
      </div>
    );
  }
  return (
    <ol className="space-y-2">
      {events.map((e, i) => (
        <li
          key={`${e.type}-${i}`}
          className={cls(
            "rounded-md border px-3 py-2 text-sm",
            e.type === "error"
              ? "border-destructive/40 bg-destructive/5"
              : e.type === "session.terminal"
              ? "border-primary/40 bg-primary/5"
              : "border-border/60",
          )}
        >
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground mr-2">
            <Chevron className="mr-1.5" />
            {e.type}
          </span>
          <span className="text-foreground">{summarise(e)}</span>
        </li>
      ))}
    </ol>
  );
}

function summarise(e: EventEnvelope): string {
  switch (e.type) {
    case "session.created":
      return "Session opened.";
    case "scan.completed":
      return "Catalogue scan complete.";
    case "recommendation.ready": {
      const p = e.payload as unknown as { winner?: string; confidence?: string };
      return `Recommendation ready: ${p.winner ?? "(none)"} (${p.confidence ?? "?"} confidence).`;
    }
    case "parameters.requested": {
      const p = e.payload as unknown as { pattern_name?: string };
      return `Parameters needed for ${p.pattern_name ?? "the chosen pattern"}.`;
    }
    case "plan.ready":
      return "What-if diff ready.";
    case "deploy.started":
      return "Deploy started.";
    case "deploy.status": {
      const p = e.payload as unknown as { state?: string };
      return `Deploy status: ${p.state ?? "(unknown)"}.`;
    }
    case "session.terminal":
      return "Session terminated.";
    case "error": {
      const p = e.payload as unknown as { error?: string };
      return p.error ?? "An error occurred.";
    }
    default:
      return e.type;
  }
}

function cls(...parts: (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}
